import * as React from "react";
import styled from "@emotion/styled";
import {
  useForm,
  useFieldArray,
  type Control,
  useController,
} from "react-hook-form";
import { FlexRow, Input, InputPre, ScreenReaderText, Table } from "atoms";
import {
  useUpdateProductVariants,
  type UpdateProductVariantsReq,
} from "../../api/useUpdateProductVariants";
import type { ProductUpdate, StepProps, TemplateVariant } from "../../types";
import { ProductWizardStep } from "../ProductWizardStep";

function StepPricing({ state, dispatch, moveBack }: StepProps) {
  const { isLoading, mutate } = useUpdateProductVariants(state.product.id);

  // Use the product's template data to create a map of each variant by variant id which we'll use
  // when rendering each row of the pricing table to display minimum and recommended prices.
  const variantMap = React.useMemo(
    () =>
      state.productTemplate.variants.reduce<Record<string, TemplateVariant>>(
        (acc, cur) => {
          acc[cur.id] = cur;
          return acc;
        },
        {},
      ),
    [state.productTemplate],
  );

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm<UpdateProductVariantsReq>({
    defaultValues: {
      variants: state.product.variants.map((v) => ({
        ...variantMap[v.templateVariantId ?? ""],
        id: v.id,
        variantId: v.templateVariantId,
        priceInCents: Number(v.price.amount) * 100,
        title: v.title,
      })),
    },
  });

  const { fields } = useFieldArray({ control, name: "variants" });

  const moveNext = () => dispatch({ type: "moveNext" });
  const onSubmit = handleSubmit((formData) => {
    if (isDirty) {
      mutate(formData, {
        onSuccess: moveNext,
      });
    } else {
      moveNext();
    }
  });

  const hasVariants = fields.length > 1;

  return (
    <ProductWizardStep
      stepTitle="Pricing"
      state={state}
      moveBack={moveBack}
      moveBackDisabled={isDirty}
      nextStep={onSubmit}
      nextStepDisabled={isLoading}
      isForm
    >
      <STable>
        <thead>
          <tr>
            {hasVariants && <td>Variant</td>}
            <td>Min. Price</td>
            <td>List Price</td>
            <td>You Make</td>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => (
            <VariantRow
              key={field.id}
              {...field}
              index={index}
              error={errors.variants?.[index]?.priceInCents?.message}
              hasVariants={hasVariants}
              control={control}
            />
          ))}
        </tbody>
      </STable>
    </ProductWizardStep>
  );
}

// Create a formatter to always show number as USD without the dollar sign
const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});
const formatCents = (num: number) =>
  formatter
    .format(num / 100)
    .replace("$", "")
    .replace("NaN", "-");

type VariantRowProps = ProductUpdate["variants"][number] & {
  error?: string;
  hasVariants: boolean;
  index: number;
  control: Control<UpdateProductVariantsReq>;
  title: string;
  minimumPriceCents: number;
  recommendedPriceCents: number;
};

function VariantRow({
  control,
  error,
  hasVariants,
  index,
  priceInCents,
  title,
  minimumPriceCents,
  recommendedPriceCents,
}: VariantRowProps) {
  const {
    field: { onBlur, onChange, ref },
  } = useController({
    control,
    name: `variants.${index}.priceInCents`,
    rules: {
      min: {
        value: minimumPriceCents,
        message: `The minimum price is $${formatCents(minimumPriceCents)}`,
      },
      validate: (value) => !Number.isNaN(value) || "This value is not a number",
    },
  });

  const [profitCents, setProfitCents] = React.useState(
    priceInCents - minimumPriceCents,
  );

  const parseOnChange: React.ChangeEventHandler<HTMLInputElement> = ({
    currentTarget,
  }) => {
    // Convert input string to number in cents, but in case of empty string, substitute with our own
    // invalid value instead because we want empty value to get caught by validation instead of
    // being okay with a zero. `Number("") === 0`
    const value =
      Number(currentTarget.value === "" ? "NaN" : currentTarget.value) * 100;
    // Update form hook with new value in cents, let validation trigger if value is NaN
    onChange(value);
    // Update profit value in cents, formatter will handle if value is NaN
    setProfitCents(value - minimumPriceCents);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    // If the user is trying to enter a single character that isn't a digit or a decimal point, and
    // they aren't holding a modifier key (e.g. `CMD-a` to select all, `CTRL-p` to paste a value),
    // then prevent the event. This effectively turns the text input into a less aggressively
    // limited number input to be as accessible as possible. Form validation should handle anything
    // else that sneaks through this filter.
    if (/^[^\d.]$/.test(event.key) && !(event.ctrlKey || event.metaKey)) {
      event.preventDefault();
    }
  };

  return (
    <tr>
      {hasVariants && <th>{title}</th>}
      <td>
        <FlexRow gap="0">
          <InputPre>$</InputPre>
          <Input
            value={formatCents(minimumPriceCents)}
            readOnly
            tabIndex={-1}
          />
        </FlexRow>
      </td>
      <td>
        <ScreenReaderText as="label">Price adjustment</ScreenReaderText>
        <FlexRow gap="0" title={error}>
          <InputPre>$</InputPre>
          <Input
            autoFocus={index === 0}
            defaultValue={formatCents(priceInCents)}
            isInvalid={!!error}
            placeholder={formatCents(recommendedPriceCents)}
            onBlur={onBlur}
            onChange={parseOnChange}
            onKeyDown={onKeyDown}
            ref={ref}
          />
        </FlexRow>
      </td>
      <td>
        <FlexRow gap="0">
          <InputPre>$</InputPre>
          <Input value={formatCents(profitCents)} readOnly tabIndex={-1} />
        </FlexRow>
      </td>
    </tr>
  );
}

StepPricing.displayName = "Pricing";

const STable = styled(Table)(({ theme }) => ({
  width: "max-content",
  td: {
    fontSize: 15,
    verticalAlign: "center",
    minWidth: "calc(100% / 4)",
    width: "max-content",
  },
  "thead td": { fontSize: 16, fontWeight: "bold", color: theme.colors.fg },
}));

// TODO: can we use this component by splitting up the fields array by option?
// const TableSubhead = styled.th(({ theme }) => ({
//   backgroundColor: theme.colors.fg10,
//   color: theme.colors.fg,
// }));

export { StepPricing, VariantRow };
